import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import AppShell from '../../components/layout/AppShell';
import { useLang } from '../../contexts/LangContext';
import { authAPI } from '../../utils/api';

const Q = (s) => `"${s}"`;
const APOS = "\u2019"; // typographic apostrophe — safe in JSX text

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [user, setUser] = useState(null);
  useEffect(() => { authAPI.getMe().then((r) => setUser(r.data)).catch(() => {}); }, []);
  const today = new Date().toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US');

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Gizlilik & KVKK Aydınlatma Metni' : 'Privacy Policy'}>
      <div className="max-w-3xl mx-auto p-4 sm:p-8 md:p-12 space-y-6 text-stone-800">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900" data-testid="legal-back">
          <ArrowLeft className="w-4 h-4" /> {lang === 'tr' ? 'Geri' : 'Back'}
        </button>

        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-800 text-xs font-semibold uppercase tracking-wider">
            <Shield className="w-3 h-3" /> KVKK · GDPR
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lang === 'tr' ? 'Gizlilik Politikası ve Aydınlatma Metni' : 'Privacy Policy'}
          </h1>
          <p className="text-sm text-stone-500">{lang === 'tr' ? 'Son güncelleme' : 'Last updated'}: {today}</p>
        </header>

        {lang === 'tr' ? (
          <article className="prose prose-stone max-w-none leading-relaxed space-y-5">
            <p>
              <strong>Hafızım</strong> {Q('Uygulama')} olarak kişisel verilerinizin güvenliği bizim için önceliklidir.
              Bu aydınlatma metni, 6698 sayılı Kişisel Verilerin Korunması Kanunu {Q('KVKK')} ve Avrupa Birliği Genel Veri Koruma Tüzüğü {Q('GDPR')} kapsamında,
              Veri Sorumlusu sıfatıyla kişisel verilerinizin işlenmesine ilişkin sizi bilgilendirmek üzere hazırlanmıştır.
            </p>

            <h2 className="text-xl font-bold mt-6">1. Veri Sorumlusu</h2>
            <p>
              Hafızım, <strong>Servet Altaş</strong> tarafından bağımsız olarak işletilen, kâr amacı gütmeyen bir eğitim/öğretim
              uygulamasıdır. Veri Sorumlusu sıfatıyla iletişim bilgileri:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Ad-Soyad: Servet Altaş</li>
              <li>E-posta (KVKK başvuruları dahil): <a href="mailto:iletisim@hafizim.com" className="text-teal-700 underline">iletisim@hafizim.com</a></li>
              <li>Web başvuru formu: <a href="/iletisim" className="text-teal-700 underline">hafizim.com/iletisim</a></li>
              <li>Faaliyet konusu: Bireysel/ücretsiz dini eğitim aracı (Kur&apos;an okuma, dinleme, ezber)</li>
            </ul>

            <h2 className="text-xl font-bold mt-6">2. İşlenen Kişisel Veri Kategorileri</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Kimlik bilgileri:</strong> Ad-soyad (kullanıcı tarafından girilen takma ad olabilir)</li>
              <li><strong>İletişim bilgileri:</strong> E-posta adresi (giriş ve doğrulama için)</li>
              <li><strong>Hesap güvenliği:</strong> Şifre (bcrypt ile geri-döndürülemez şekilde özetlenir, asla düz metin saklanmaz)</li>
              <li><strong>Kullanım verileri:</strong> Ezberleme ilerlemesi, ayet/sayfa istatistikleri, tercih edilen mod/dil, başarı rozetleri</li>
              <li><strong>Ses verisi:</strong> Mikrofonla okunan ayet sesleri — yalnızca anlık olarak konuşmadan metne dönüştürülmesi için sunucuya gönderilir, kalıcı olarak saklanmaz</li>
              <li><strong>Teknik veriler:</strong> Çerez/yerel depolama tercihleri (mod, dil, oturum), IP adresi (oturum güvenliği için geçici)</li>
              <li><strong>Bildirim verileri:</strong> Web Push aboneliği için tarayıcı tarafından üretilen anonim endpoint</li>
            </ul>

            <h2 className="text-xl font-bold mt-6">3. İşleme Amaçları ve Hukuki Sebepleri</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Hesap oluşturma, kimlik doğrulama ve oturum yönetimi — <em>sözleşmenin kurulması/ifası</em> (KVKK m.5/2-c)</li>
              <li>Ezber, hatim, hafızlık ilerleme takibi ve istatistik gösterimi — <em>sözleşmenin ifası</em></li>
              <li>Ses verisinin metne dönüştürülerek doğruluk geri bildirimi sunulması — <em>açık rızanız</em> (m.5/1)</li>
              <li>Günlük ayet / hatırlatma push bildirimleri — <em>açık rızanız</em> (m.5/1)</li>
              <li>Hukuki yükümlülüklerin yerine getirilmesi — <em>kanunlarda öngörülmesi</em> (m.5/2-a)</li>
              <li>Hizmet kalitesinin iyileştirilmesi ve hata teşhisi — <em>meşru menfaat</em> (m.5/2-f)</li>
            </ul>

            <h2 className="text-xl font-bold mt-6">4. Veri Aktarımı (Üçüncü Taraflar)</h2>
            <p>Verileriniz aşağıdaki hizmet sağlayıcılarla, yalnızca hizmetin sunulabilmesi için ve gerekli minimum kapsamda paylaşılır:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>MongoDB Atlas</strong> (veri tabanı barındırma)</li>
              <li><strong>OpenAI</strong> (mikrofon ses-metin dönüştürme &amp; sesli okuma — anlık, saklanmaz)</li>
              <li><strong>Resend</strong> (e-posta doğrulama / şifre sıfırlama mesajları)</li>
              <li><strong>alquran.cloud / everyayah.com / mp3quran.net</strong> (Kuran metni ve ses içeriği — yalnızca uygulama tarafından çağrılır, kullanıcı verisi gönderilmez)</li>
            </ul>
            <p>Yukarıdaki hizmet sağlayıcılar yurt dışında bulunabilir. Bu aktarım için açık rıza alınmakta ve yeterli koruma sağlanmaktadır.</p>

            <h2 className="text-xl font-bold mt-6">5. Saklama Süresi</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Hesap aktif olduğu sürece kullanıcı verileri saklanır.</li>
              <li>Hesap silindiğinde tüm kişisel verileriniz 30 gün içinde silinir.</li>
              <li>Ses verisi sunucuda kalıcı olarak saklanmaz; sadece metne dönüştürme süresi boyunca tutulur.</li>
              <li>Yasal yükümlülük gereği saklanması zorunlu olan veriler (varsa) ilgili yasal süre kadar tutulur.</li>
            </ul>

            <h2 className="text-xl font-bold mt-6">6. KVKK Madde 11 — Veri Sahibi Haklarınız</h2>
            <p>Aşağıdaki haklara sahipsiniz:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
              <li>İşlenmişse buna ilişkin bilgi talep etme</li>
              <li>İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme</li>
              <li>Yurt içinde/yurt dışında aktarıldığı üçüncü kişileri bilme</li>
              <li>Eksik/yanlış işlenmiş verilerin düzeltilmesini isteme</li>
              <li>KVKK 7. madde uyarınca <strong>silinmesini veya yok edilmesini isteme</strong></li>
              <li>Düzeltme/silme işlemlerinin verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme</li>
              <li>İşlenen verilerin münhasıran otomatik sistemler ile analizine itiraz etme</li>
              <li>Hukuka aykırı işleme sebebiyle zarara uğranması halinde tazminat talep etme</li>
            </ul>
            <p>
              Bu hakları kullanmak için <strong>Ayarlar &gt; Hesabım &gt; Verilerimi İndir</strong> veya
              <strong> Hesabımı Sil</strong> seçeneklerini kullanabilirsiniz; ya da
              <a href="mailto:iletisim@hafizim.com" className="text-teal-700 underline"> iletisim@hafizim.com</a> adresine yazılı başvuruda bulunabilirsiniz.
            </p>

            <h2 className="text-xl font-bold mt-6">7. Çocukların Verileri</h2>
            <p>
              Hafızım, 13 yaşın altındaki kullanıcıların verilerini bilerek toplamaz. Çocuk Modu, ebeveyn gözetiminde kullanılmak üzere
              tasarlanmıştır. 13 yaş altı bir çocuğun veri girdiğini fark edersek, hesap derhal silinir. Çocuklara ait herhangi bir veri
              tespiti durumunda lütfen bizimle iletişime geçin.
            </p>

            <h2 className="text-xl font-bold mt-6">8. Çerezler ve Yerel Depolama</h2>
            <p>
              Tercihlerinizi (mod, dil, son okunan sayfa) hatırlamak için <strong>localStorage</strong> kullanırız. Tarayıcı içinde
              saklanan bu veriler hiçbir sunucuya gönderilmez. Detaylı bilgi için
              <a href="/cerez-politikasi" className="text-teal-700 underline"> Çerez Politikası</a>{APOS}na bakabilirsiniz.
            </p>

            <h2 className="text-xl font-bold mt-6">9. Güvenlik</h2>
            <p>
              Şifreler bcrypt ile özetlenir. İletişim HTTPS üzerinden şifrelidir. Veritabanına erişim sıkı denetim altındadır.
              Buna rağmen internet üzerinden iletilen hiçbir veri %100 güvenli değildir; makul önlemlere rağmen oluşabilecek
              risklerin farkında olarak hizmeti kullandığınızı kabul edersiniz.
            </p>

            <h2 className="text-xl font-bold mt-6">10. Değişiklikler</h2>
            <p>
              Bu metin gerektiğinde güncellenebilir. Önemli değişikliklerde uygulama içinde bildirim yapılır. Devam eden kullanım,
              güncellenmiş metni kabul ettiğiniz anlamına gelir.
            </p>

            <h2 className="text-xl font-bold mt-6">11. İletişim</h2>
            <p>Sorularınız için: <a href="mailto:iletisim@hafizim.com" className="text-teal-700 underline">iletisim@hafizim.com</a></p>

            <h2 className="text-xl font-bold mt-6">12. Tanıtım/Pazarlama Mesajları (İYS)</h2>
            <p>
              Hafızım şu an tanıtım/pazarlama e-postası göndermemektedir. Gelecekte gönderim yapılması durumunda
              6563 sayılı Elektronik Ticaretin Düzenlenmesi Hakkında Kanun ve İleti Yönetim Sistemi (İYS) mevzuatına
              uygun olarak <strong>İYS kaydı</strong> yapılacak ve yalnızca <strong>açık rıza vermiş</strong> kullanıcılara
              gönderilecektir. Tercihinizi istediğiniz zaman <em>Ayarlar &gt; Açık Rıza Tercihleri</em> bölümünden değiştirebilirsiniz.
            </p>
          </article>
        ) : (
          <article className="prose prose-stone max-w-none leading-relaxed space-y-5">
            <p><strong>Hafızım</strong> respects your privacy. This notice describes how we collect, use and protect your personal data under KVKK (Turkish DPA) and GDPR.</p>
            <h2 className="text-xl font-bold mt-6">1. Data we collect</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name, email, hashed password</li>
              <li>Memorization/usage progress, preferences, badges</li>
              <li>Voice audio — sent transiently to STT, never stored permanently</li>
              <li>Local storage preferences (mode, language)</li>
            </ul>
            <h2 className="text-xl font-bold mt-6">2. Purposes &amp; legal bases</h2>
            <p>Account management, progress tracking (contract), STT and push notifications (consent), service improvement (legitimate interest).</p>
            <h2 className="text-xl font-bold mt-6">3. Third parties</h2>
            <p>MongoDB Atlas, OpenAI (STT/TTS), Resend (email), alquran.cloud / mp3quran.net (content).</p>
            <h2 className="text-xl font-bold mt-6">4. Your rights</h2>
            <p>Access, correction, deletion, portability, objection. Use Settings &gt; My Account &gt; Export Data / Delete Account, or email <a href="mailto:iletisim@hafizim.com" className="text-teal-700 underline">iletisim@hafizim.com</a>.</p>
            <h2 className="text-xl font-bold mt-6">5. Children</h2>
            <p>We do not knowingly collect data from children under 13. Kids Mode is intended for use under parental supervision.</p>
          </article>
        )}
      </div>
    </AppShell>
  );
};

export default PrivacyPolicy;
